<template>
  <article class="book-shelf">
    <div class="bubbleBorderWrapper">
      <section class="contentWrapper bubbleBorder books">
        <div class="bubbleBorderBubble biggerBubble">
          <ContentTypeIcon :content-type="ContentType.BOOKS" />
        </div>
        <h1>{{ $t('book-shelf.title') }}</h1>
        <div v-if="errors.length === 0" class="book-shelf">
          <BooksClassFilter
            v-if="!loading"
            :level-intervals="levelIntervals"
            :user-level="userClass"
            :is-teacher="isTeacher"
            v-on:classIntervalChanged="onClassFilterChange"
          />
          <LoadingAnimation v-else />

          <h2 class="level-name">{{ levelName }}</h2>
          <ul class="books-grid">
            <li
              class="book-list-item"
              v-for="book in books"
              :key="book.id"
              :hidden="!showBook(book.minClass, book.maxClass)"
            >
              <BookThumb
                :book-data="book"
                :has-level-data="levelsData.length > 0"
                :level-class-name="
                  getBookLevelName(book.minClass, book.maxClass)
                "
              />
            </li>
          </ul>
        </div>
        <div class="error-messages" v-else>
          <p v-for="(error, index) in errors" :key="index">
            {{ error.message }}
          </p>
        </div>
      </section>
    </div>
  </article>
</template>

<script>
import BooksClassFilter from './BooksClassFilter'
import BookThumb from './BookThumb'
import ContentTypeIcon from '@/components/ui/ContentTypeIcon'
import LoadingAnimation from '../LoadingAnimation'
import { AuthStoreNamespacedTypes } from '@/store/auth'
import { BookStoreNamespacedTypes } from '@/store/books'
import { LevelsStoreNamespacedTypes } from '@/store/levels'
import { ContentType, UserType } from '@/enums'

export default {
  name: 'BookShelf',
  components: {
    BooksClassFilter,
    BookThumb,
    ContentTypeIcon,
    LoadingAnimation,
  },
  data() {
    return {
      ContentType,
      levelName: '',
      selectedLevelIntervals: [
        this.$config.classMinLevel,
        this.$config.classXMaxLevel,
      ],
    }
  },
  computed: {
    /**
     * @returns {Array}
     */
    books() {
      return this.$store.getters[BookStoreNamespacedTypes.getters.GET_ALL]()
    },

    /**
     * @returns {Array}
     */
    errors() {
      return this.$store.getters[
        BookStoreNamespacedTypes.getters.GET_ERRORS
      ]().concat(
        this.$store.getters[LevelsStoreNamespacedTypes.getters.GET_ERRORS]()
      )
    },

    /**
     * @returns {boolean}
     */
    isTeacher() {
      return (
        this.$store.getters[
          AuthStoreNamespacedTypes.getters.GET_USER_TYPE
        ]() === UserType.TEACHER
      )
    },

    /**
     * @returns {Array}
     */
    levelsData() {
      return this.$store.getters[LevelsStoreNamespacedTypes.getters.GET_ALL]()
    },

    /**
     * @returns {Array}
     */
    levelIntervals() {
      return this.$store.getters[
        LevelsStoreNamespacedTypes.getters.GET_LEVEL_INTERVALS
      ]()
    },

    /**
     * @returns {boolean}
     */
    loading() {
      return !this.books.length > 0 || !this.levelIntervals
    },

    /**
     * @returns {number}
     */
    showMinClass() {
      return this.selectedLevelIntervals[0]
        ? this.selectedLevelIntervals[0]
        : this.$config.classMinLevel
    },

    /**
     * @returns {number}
     */
    showMaxClass() {
      return this.selectedLevelIntervals[1]
        ? this.selectedLevelIntervals[1]
        : this.$config.classXMaxLevel
    },

    /**
     * @returns {number}
     */
    userClass() {
      return this.$store.getters[
        AuthStoreNamespacedTypes.getters.GET_USER_CLASS
      ]()
    },
  },
  methods: {
    /**
     * Returns a boolean on whether or not the book should be show,
     * based on whether or not its level range is within or
     * overlaps with the filter level range.
     *
     * @param {number} bookMinClass
     * @param {number} bookMaxClass
     * @returns {boolean}
     */
    showBook(bookMinClass, bookMaxClass) {
      return (
        this.showMinClass <= bookMaxClass && this.showMaxClass >= bookMinClass
      )
    },
    /**
     * Handles the changes when filter is changed
     * @param {number} minClass
     * @param {number} maxClass
     * @param {string} name
     */
    onClassFilterChange(minClass, maxClass, name) {
      this.selectedLevelIntervals = [minClass, maxClass]
      this.levelName = name
    },

    /**
     * Gets the human friendly class level name for each book.
     *
     * @param {number} minClass
     * @param {number} maxClass
     * @returns {string}
     */
    getBookLevelName(minClass, maxClass) {
      if (!this.levelsData) {
        return ''
      }

      let bookLevelData = this.levelsData.filter(levelData => {
        let level = levelData['Level']['id']

        return level === minClass || level === maxClass
      })

      // return empty string if no matching levels are found
      if (bookLevelData.length <= 0) {
        return ''
      }

      // Min and Max class/level differs, meaning we have a range
      if (bookLevelData.length > 1) {
        return (
          bookLevelData[0]['Level']['short'] +
          ' - ' +
          bookLevelData[1]['Level']['name']
        )
      }

      // Only one level is assigned
      return bookLevelData[0]['Level']['name']
    },
  },
  mounted() {
    this.$store.dispatch(BookStoreNamespacedTypes.actions.FETCH_ALL)
    this.$store.dispatch(LevelsStoreNamespacedTypes.actions.FETCH_ALL)
    this.$store.dispatch(
      LevelsStoreNamespacedTypes.actions.FETCH_LEVEL_INTERVALS
    )
  },
}
</script>

<style lang="scss" scoped>
.level-name {
  margin: 2em 0 0;
}

.books-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(10rem, 1fr));
  grid-gap: 4em;
  padding: 0;
  margin: 0.7em 0 0;
  list-style: none;

  @media (max-width: $minWidthDesktop) {
    grid-template-columns: repeat(auto-fill, minmax(20%, 1fr));
  }

  @media (max-width: $minWidthSmallTablet) {
    grid-template-columns: repeat(auto-fill, minmax(25%, 1fr));
    grid-gap: 2em;
  }

  @media (max-width: $minWidthPhablet) {
    grid-template-columns: repeat(auto-fill, minmax(40%, 1fr));
  }

  .book-list-item {
    display: inline-block;
    padding: 0;
    list-style: none;
    background-image: none;

    &[hidden] {
      display: none;
    }
  }
}

.error-messages {
  color: $errorText;
}
</style>
